import memoize from 'transmute/memoize';
import I18n from 'I18n';
import getLangLocale from 'I18n/utils/getLangLocale';
import { reportError } from 'automation-ui-core/errors/utils/reportError';

/**
 * Uses Intl.DateTimeFormat's formatToParts() to determine how a date would be
 * formatted without a year in the user's locale.  Our I18n tools use hardcoded,
 * manually localized date formats that all include the year so we can't use it
 * alone for this.
 *
 * Enabling the placeholder parameter produces a human-readable version of the
 * format for a text input placeholder.
 */
export const getRecurringDateMomentFormat = memoize(({
  locale = getLangLocale(),
  placeholder = false
} = {}) => {
  const dateTimeFormat = getRecurringDateIntlFormatObject({
    locale
  });
  let monthFormat;
  let dayFormat;
  if (placeholder) {
    // Borrow strings from UIDateInput
    monthFormat = I18n.text('ui.datePicker.placeholder.month');
    dayFormat = I18n.text('ui.datePicker.placeholder.day');
  } else {
    // Some languages don't support padding to two digits
    const {
      month,
      day
    } = dateTimeFormat.resolvedOptions();
    monthFormat = month === '2-digit' ? 'MM' : 'M';
    dayFormat = day === '2-digit' ? 'DD' : 'D';
  }

  // The actual date isn't relevant here. Using a timestamp of 0 for consistency
  const parts = dateTimeFormat.formatToParts(new Date(0));
  return parts.map(({
    type,
    value
  }) => {
    // All part types are documented in MDN:
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/formatToParts
    if (type === 'month') {
      return monthFormat;
    } else if (type === 'day') {
      return dayFormat;
    } else if (type === 'literal') {
      return value;
    } else {
      reportError({
        error: new Error("Encountered an unexpected date format part, check this error's tags for details"),
        tags: {
          formatType: type,
          formatLocale: locale
        }
      });
      return '';
    }
  }).join('');
}, ({
  locale,
  placeholder
} = {}) => `${locale}-${placeholder}`);
export function getRecurringDateIntlFormatObject({
  locale = getLangLocale()
} = {}) {
  return new Intl.DateTimeFormat(locale, {
    month: '2-digit',
    day: '2-digit',
    numberingSystem: 'latn' // Moment doesn't support other numbering systems
  });
}